import {
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  ChevronDown,
  Clock,
  Globe2,
  LogIn,
  Mail,
  MapPin,
  Moon,
  Phone,
  Sun,
  type LucideIcon,
} from 'lucide-react'
import { useState, type CSSProperties, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { LanguagePicker } from '@/features/preferences/components/PreferenceControls'
import { resolvedTheme, usePreferences } from '@/features/preferences/store'
import { intlLocale, tAny } from '@/i18n'
import { APP_NAME } from '@/utils/brand'
import { cn } from '@/utils/cn'
import { formatDate, initials } from '@/utils/format'
import { BLOCK_META, HIGHLIGHT_ICON_COMPONENTS } from '../blocks'
import type { HomepageContact, PublicAdmissions, PublicBlock, PublicHomepage } from '../types'
import './homepage.css'

export interface HomepageViewProps {
  page: PublicHomepage
  /** undefined while loading, null when the school takes no online applications. */
  admissions: PublicAdmissions | null | undefined
  /** Rendered inside the editor: explains the blocks visitors would not see. */
  preview?: boolean
  /** "Sign in" by default; a signed-in visitor goes back to their space. */
  account?: { href: string; label: string }
  websiteUrl?: string | null
}

/**
 * The school's public homepage. Layout follows the width of its container (container queries), so
 * the editor's phone preview shows exactly what a phone shows.
 */
export function HomepageView({ page, admissions, preview = false, account, websiteUrl }: HomepageViewProps) {
  const { t } = useTranslation('homepage')
  const canApply = admissions?.open === true
  const style = page.accentColor ? ({ '--hp-accent': page.accentColor } as CSSProperties) : undefined
  const signIn = account ?? { href: '/login', label: t('Sign in') }

  return (
    <div
      className={cn(
        '@container min-h-full bg-canvas [overflow-wrap:anywhere] text-ink',
        page.accentColor && 'hp-accent',
      )}
      style={style}
    >
      <Header page={page} canApply={canApply} signIn={signIn} />
      <main id="top">
        <Hero page={page} canApply={canApply} signIn={signIn} />
        {page.blocks.map((block, i) => (
          <BlockView
            key={block.id}
            block={block}
            tone={i % 2 === 0 ? 'plain' : 'surface'}
            page={page}
            admissions={admissions}
            preview={preview}
          />
        ))}
      </main>
      <Footer page={page} websiteUrl={websiteUrl} />
    </div>
  )
}

function SchoolMark({ page, className }: { page: PublicHomepage; className?: string }) {
  const { logoUrl, name, shortName } = page.school
  return logoUrl ? (
    <img
      src={logoUrl}
      alt=""
      className={cn(
        'size-10 shrink-0 rounded-xl border border-line bg-white object-contain p-0.5',
        className,
      )}
    />
  ) : (
    <span
      aria-hidden="true"
      className={cn(
        'bg-brand-gradient grid size-10 shrink-0 place-items-center rounded-xl text-sm font-semibold text-white shadow-card',
        className,
      )}
    >
      {initials(shortName ?? name)}
    </span>
  )
}

function ThemeToggle({ className }: { className?: string }) {
  const { t } = useTranslation('homepage')
  const { theme, setTheme } = usePreferences()
  const dark = resolvedTheme(theme) === 'dark'
  return (
    <button
      type="button"
      onClick={() => setTheme(dark ? 'light' : 'dark')}
      className={cn(
        'grid size-9 shrink-0 place-items-center rounded-control text-ink-soft hover:bg-surface-sunken hover:text-ink',
        className,
      )}
      aria-label={dark ? t('Light theme') : t('Dark theme')}
    >
      {dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </button>
  )
}

function Header({
  page,
  canApply,
  signIn,
}: {
  page: PublicHomepage
  canApply: boolean
  signIn: { href: string; label: string }
}) {
  const { t } = useTranslation('homepage')
  return (
    <header className="sticky top-0 z-30 border-b border-line/70 bg-surface/85 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-2 px-3 @md:gap-3 @3xl:px-6">
        <a href="#top" className="flex min-w-0 flex-1 items-center gap-2.5">
          <SchoolMark page={page} className="size-9" />
          <span className="min-w-0 truncate text-[0.9375rem] font-semibold">{page.school.name}</span>
        </a>
        <LanguagePicker compact className="shrink-0" />
        <ThemeToggle className="hidden @xl:grid" />
        {canApply ? (
          <Button asChild size="sm" variant="secondary" className="hidden @3xl:inline-flex">
            <Link to="/inscription">{t('Apply')}</Link>
          </Button>
        ) : null}
        <Button asChild size="sm" className="px-2.5 @md:px-3">
          <Link to={signIn.href} aria-label={signIn.label}>
            <LogIn />
            <span className="hidden @sm:inline">{signIn.label}</span>
          </Link>
        </Button>
      </div>
    </header>
  )
}

function Hero({
  page,
  canApply,
  signIn,
}: {
  page: PublicHomepage
  canApply: boolean
  signIn: { href: string; label: string }
}) {
  const { t } = useTranslation('homepage')
  const title = page.heroTitle ?? page.school.name
  const subtitle = page.heroSubtitle ?? page.school.motto
  const cover = page.coverUrl
  return (
    <section className={cn('relative isolate overflow-hidden text-white', !cover && 'bg-brand-gradient')}>
      {cover ? (
        <>
          <img src={cover} alt="" className="absolute inset-0 -z-10 size-full object-cover" />
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 bg-gradient-to-t from-black/80 via-black/55 to-black/30"
          />
        </>
      ) : null}
      <div className="mx-auto max-w-6xl px-4 py-16 @3xl:px-6 @3xl:py-24 @5xl:py-28">
        <div className="max-w-3xl">
          <h1 className="text-3xl leading-tight font-semibold tracking-tight text-white @xl:text-4xl @3xl:text-5xl">
            {title}
          </h1>
          {subtitle ? <p className="mt-5 max-w-2xl text-base text-white/85 @xl:text-lg">{subtitle}</p> : null}
          <div className="mt-8 grid grid-cols-1 gap-3 @md:flex @md:flex-wrap">
            {canApply ? (
              <Button
                asChild
                size="lg"
                variant="secondary"
                className="h-auto min-h-11 border-transparent bg-white py-2 whitespace-normal text-brand-700 hover:bg-white/90 hover:text-brand-800"
              >
                <Link to="/inscription">
                  {t('Apply online')}
                  <ArrowRight />
                </Link>
              </Button>
            ) : null}
            <Button
              asChild
              size="lg"
              variant="secondary"
              className="h-auto min-h-11 border-white/40 bg-white/10 py-2 whitespace-normal text-white hover:border-white/60 hover:bg-white/20 hover:text-white"
            >
              <Link to={signIn.href}>
                <LogIn />
                {signIn.label}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}

function Section({
  id,
  title,
  tone,
  intro,
  children,
}: {
  id: string
  title: string
  tone: 'plain' | 'surface'
  intro?: string | null
  children?: ReactNode
}) {
  const headingId = `hp-${id}-title`
  return (
    <section
      id={`hp-${id}`}
      aria-labelledby={headingId}
      className={cn('scroll-mt-16', tone === 'surface' && 'border-y border-line bg-surface')}
    >
      <div className="mx-auto max-w-6xl px-4 py-14 @3xl:px-6 @3xl:py-20">
        <h2 id={headingId} className="text-2xl font-semibold tracking-tight @3xl:text-3xl">
          {title}
        </h2>
        {intro ? <Paragraphs text={intro} className="mt-3 max-w-3xl text-ink-soft" /> : null}
        {children}
      </div>
    </section>
  )
}

function Paragraphs({ text, className }: { text: string; className?: string }) {
  return (
    <div className={cn('space-y-4', className)}>
      {text.split(/\n{2,}/).map((p, i) => (
        <p key={i} className="whitespace-pre-line">
          {p}
        </p>
      ))}
    </div>
  )
}

function BlockView({
  block,
  tone,
  page,
  admissions,
  preview,
}: {
  block: PublicBlock
  tone: 'plain' | 'surface'
  page: PublicHomepage
  admissions: PublicAdmissions | null | undefined
  preview: boolean
}) {
  const { t } = useTranslation('homepage')
  const title = block.title ?? tAny(BLOCK_META[block.type].defaultTitle)
  switch (block.type) {
    case 'text':
      return (
        <Section id={block.id} title={title} tone={tone}>
          {block.body ? <Paragraphs text={block.body} className="mt-5 max-w-3xl text-ink-soft" /> : null}
        </Section>
      )
    case 'list':
      return (
        <Section id={block.id} title={title} tone={tone} intro={block.body}>
          <ul className="mt-8 grid grid-cols-1 gap-3 @2xl:grid-cols-2">
            {block.items.map((item, i) => (
              <li
                key={i}
                className="flex min-w-0 items-start gap-3 rounded-card border border-line bg-canvas p-4"
              >
                <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-brand-600" aria-hidden="true" />
                <span className="min-w-0 text-sm text-ink-soft">{item}</span>
              </li>
            ))}
          </ul>
        </Section>
      )
    case 'highlights':
      return (
        <Section id={block.id} title={title} tone={tone}>
          <ul
            className={cn(
              'mt-8 grid grid-cols-1 gap-4 @2xl:grid-cols-2',
              block.highlights.length % 3 === 0 ? '@5xl:grid-cols-3' : '@5xl:grid-cols-4',
            )}
          >
            {block.highlights.map((h, i) => {
              const Icon =
                (HIGHLIGHT_ICON_COMPONENTS as Partial<Record<string, LucideIcon>>)[h.icon] ?? CheckCircle2
              return (
                <li key={i} className="min-w-0 rounded-card border border-line bg-canvas p-5 shadow-card">
                  <span className="bg-brand-gradient grid size-11 place-items-center rounded-xl text-white shadow-card">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 text-lg font-semibold">{h.title}</h3>
                  {h.text ? <p className="mt-2 text-sm text-ink-soft">{h.text}</p> : null}
                </li>
              )
            })}
          </ul>
        </Section>
      )
    case 'programmes': {
      const series = [...new Set(block.classes.flatMap((c) => (c.series ? [c.series] : [])))]
      return (
        <Section id={block.id} title={title} tone={tone} intro={block.body}>
          {series.length ? (
            <p className="mt-5 flex flex-wrap items-center gap-2 text-sm text-ink-soft">
              <span className="font-medium">{t('Séries offered:')}</span>
              {series.map((s) => (
                <Badge key={s} tone="brand">
                  {s}
                </Badge>
              ))}
            </p>
          ) : null}
          <ul className="mt-6 grid grid-cols-1 gap-3 @md:grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4">
            {block.classes.map((c) => (
              <li
                key={c.name}
                className="flex min-w-0 items-center justify-between gap-3 rounded-card border border-line bg-canvas px-4 py-3"
              >
                <span className="min-w-0 font-medium">{c.name}</span>
                {c.series ? (
                  <Badge tone="neutral">{t('Série {{series}}', { series: c.series })}</Badge>
                ) : null}
              </li>
            ))}
          </ul>
        </Section>
      )
    }
    case 'admissions':
      return (
        <AdmissionsBlock
          id={block.id}
          title={title}
          tone={tone}
          body={block.body}
          admissions={admissions}
          preview={preview}
        />
      )
    case 'announcements':
      return (
        <Section id={block.id} title={title} tone={tone}>
          <ul className="mt-8 grid grid-cols-1 gap-4 @3xl:grid-cols-2 @5xl:grid-cols-3">
            {block.items.map((n) => (
              <NewsCard
                key={n.id}
                title={n.title}
                body={n.body}
                date={n.publishedAt}
                truncated={n.truncated}
              />
            ))}
          </ul>
        </Section>
      )
    case 'faq':
      return (
        <Section id={block.id} title={title} tone={tone}>
          <div className="mt-8 max-w-3xl divide-y divide-line rounded-card border border-line bg-canvas">
            {block.faq.map((f, i) => (
              <details key={i} className="group px-4 py-1 @md:px-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3 font-medium [&::-webkit-details-marker]:hidden">
                  <span className="min-w-0">{f.question}</span>
                  <ChevronDown
                    className="size-4 shrink-0 text-muted transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  />
                </summary>
                <Paragraphs text={f.answer} className="pb-4 text-sm text-ink-soft" />
              </details>
            ))}
          </div>
        </Section>
      )
    case 'gallery':
      return (
        <Section id={block.id} title={title} tone={tone}>
          <ul className="mt-8 grid grid-cols-1 gap-4 @md:grid-cols-2 @3xl:grid-cols-3">
            {block.images.map((img, i) => (
              <li key={i} className="min-w-0 overflow-hidden rounded-card border border-line bg-canvas">
                <img
                  src={img.url}
                  alt={img.caption ?? ''}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
                {img.caption ? <p className="px-4 py-3 text-sm text-ink-soft">{img.caption}</p> : null}
              </li>
            ))}
          </ul>
        </Section>
      )
    case 'contact':
      return (
        <ContactBlock
          id={block.id}
          title={title}
          tone={tone}
          body={block.body}
          mapUrl={block.mapUrl}
          contact={page.contact}
        />
      )
  }
}

function NewsCard({
  title,
  body,
  date,
  truncated,
}: {
  title: string
  body: string
  date: string | null
  truncated: boolean
}) {
  const { t } = useTranslation('homepage')
  const [open, setOpen] = useState(false)
  const long = truncated || body.length > 220
  return (
    <li className="flex min-w-0 flex-col rounded-card border border-line bg-canvas p-5">
      {date ? <p className="text-xs font-medium text-brand-700">{formatDate(date)}</p> : null}
      <h3 className="mt-1.5 font-semibold">{title}</h3>
      <p className={cn('mt-2 text-sm whitespace-pre-line text-ink-soft', !open && long && 'line-clamp-4')}>
        {body}
      </p>
      {long && !truncated ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-3 self-start text-sm font-medium text-brand-700 hover:underline"
          aria-expanded={open}
        >
          {open ? t('Show less') : t('Read more')}
        </button>
      ) : null}
    </li>
  )
}

function AdmissionsBlock({
  id,
  title,
  tone,
  body,
  admissions,
  preview,
}: {
  id: string
  title: string
  tone: 'plain' | 'surface'
  body: string | null
  admissions: PublicAdmissions | null | undefined
  preview: boolean
}) {
  const { t } = useTranslation('homepage')
  if (admissions === undefined) return null
  if (admissions === null) {
    return preview ? (
      <Section id={id} title={title} tone={tone}>
        <p className="mt-4 rounded-card border border-dashed border-line-strong p-4 text-sm text-muted">
          {t('Online admissions are not available for this school, so visitors will not see this block.')}
        </p>
      </Section>
    ) : null
  }
  const intro = body ?? admissions.introText
  const opensLater = !admissions.open && admissions.opensOn && new Date(admissions.opensOn) > new Date()
  return (
    <Section id={id} title={title} tone={tone}>
      <div className="mt-8 grid grid-cols-1 gap-6 rounded-card border border-line bg-canvas p-5 shadow-card @3xl:grid-cols-[1fr_auto] @3xl:items-center @3xl:p-8">
        <div className="min-w-0">
          <Badge tone={admissions.open ? 'success' : 'neutral'} dot>
            {admissions.open ? t('Applications open') : t('Applications closed')}
          </Badge>
          {admissions.academicYear ? (
            <p className="mt-3 text-lg font-semibold">
              {t('School year {{name}}', { name: admissions.academicYear.name })}
            </p>
          ) : null}
          <p className="mt-1 flex items-center gap-2 text-sm text-muted">
            <CalendarCheck className="size-4 shrink-0" aria-hidden="true" />
            <span>
              {admissions.open
                ? admissions.closesOn
                  ? t('Until {{date}}', { date: formatDate(admissions.closesOn) })
                  : t('Applications are welcome now.')
                : opensLater
                  ? t('Opening on {{date}}', { date: formatDate(admissions.opensOn) })
                  : t('Applications are closed for now.')}
            </span>
          </p>
          {admissions.open ? (
            intro ? (
              <Paragraphs text={intro} className="mt-4 text-ink-soft" />
            ) : null
          ) : admissions.closedMessage ? (
            <Paragraphs text={admissions.closedMessage} className="mt-4 text-ink-soft" />
          ) : null}
        </div>
        {admissions.open ? (
          <Button asChild size="lg" className="h-auto min-h-11 py-2 whitespace-normal">
            <Link to="/inscription">
              {t('Apply online')}
              <ArrowRight />
            </Link>
          </Button>
        ) : null}
      </div>
    </Section>
  )
}

const WEEK = [1, 2, 3, 4, 5, 6, 0]

function dayName(day: number): string {
  // 2024-01-07 was a Sunday (day 0).
  return new Intl.DateTimeFormat(intlLocale(), { weekday: 'long' }).format(new Date(2024, 0, 7 + day))
}

function hasContact(c: HomepageContact): boolean {
  return Boolean(c.address || c.phone || c.email || c.website || c.openingHours.length)
}

function ContactBlock({
  id,
  title,
  tone,
  body,
  mapUrl,
  contact,
}: {
  id: string
  title: string
  tone: 'plain' | 'surface'
  body: string | null
  mapUrl: string | null
  contact: HomepageContact
}) {
  const { t } = useTranslation('homepage')
  if (!hasContact(contact) && !body && !mapUrl) return null
  const hours = WEEK.flatMap((d) => contact.openingHours.filter((h) => h.dayOfWeek === d))
  return (
    <Section id={id} title={title} tone={tone} intro={body}>
      <div className="mt-8 grid grid-cols-1 gap-6 @3xl:grid-cols-2">
        <ul className="min-w-0 space-y-4 rounded-card border border-line bg-canvas p-5">
          {contact.address ? (
            <ContactLine icon={<MapPin />} label={t('Address')}>
              <span className="whitespace-pre-line">{contact.address}</span>
            </ContactLine>
          ) : null}
          {contact.phone ? (
            <ContactLine icon={<Phone />} label={t('Phone')}>
              <a
                href={`tel:${contact.phone.replace(/\s+/g, '')}`}
                className="hover:text-brand-700 hover:underline"
              >
                {contact.phone}
              </a>
            </ContactLine>
          ) : null}
          {contact.email ? (
            <ContactLine icon={<Mail />} label={t('Email')}>
              <a href={`mailto:${contact.email}`} className="hover:text-brand-700 hover:underline">
                {contact.email}
              </a>
            </ContactLine>
          ) : null}
          {contact.website ? (
            <ContactLine icon={<Globe2 />} label={t('Website')}>
              <a href={contact.website} rel="noopener" className="hover:text-brand-700 hover:underline">
                {contact.website.replace(/^https?:\/\//, '')}
              </a>
            </ContactLine>
          ) : null}
          {mapUrl ? (
            <li>
              <Button asChild variant="secondary" size="sm" className="whitespace-normal">
                <a href={mapUrl} target="_blank" rel="noopener noreferrer">
                  <MapPin />
                  {t('See on the map')}
                </a>
              </Button>
            </li>
          ) : null}
        </ul>
        {hours.length ? (
          <div className="min-w-0 rounded-card border border-line bg-canvas p-5">
            <h3 className="flex items-center gap-2 font-semibold">
              <Clock className="size-4 text-brand-600" aria-hidden="true" />
              {t('Opening hours')}
            </h3>
            <dl className="mt-4 divide-y divide-line text-sm">
              {hours.map((h) => (
                <div key={h.dayOfWeek} className="flex flex-wrap justify-between gap-x-4 gap-y-1 py-2">
                  <dt className="text-ink-soft capitalize">{dayName(h.dayOfWeek)}</dt>
                  <dd className={cn('tabular', h.isOpen ? 'font-medium' : 'text-muted')}>
                    {!h.isOpen
                      ? t('Closed')
                      : h.openTime && h.closeTime
                        ? `${h.openTime} – ${h.closeTime}`
                        : t('Open')}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
      </div>
    </Section>
  )
}

function ContactLine({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <li className="flex min-w-0 items-start gap-3">
      <span className="grid size-9 shrink-0 place-items-center rounded-control bg-brand-50 text-brand-600 [&_svg]:size-4">
        {icon}
      </span>
      <div className="min-w-0 text-sm">
        <p className="text-xs text-muted">{label}</p>
        <div className="mt-0.5 font-medium">{children}</div>
      </div>
    </li>
  )
}

function Footer({ page, websiteUrl }: { page: PublicHomepage; websiteUrl?: string | null }) {
  const { t } = useTranslation('homepage')
  const { contact, school } = page
  const year = new Date().getFullYear()
  return (
    <footer className="border-t border-line bg-surface">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 py-10 text-sm @3xl:grid-cols-[1fr_auto] @3xl:px-6">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-3">
            <SchoolMark page={page} />
            <div className="min-w-0">
              <p className="font-semibold">{school.name}</p>
              {school.motto ? <p className="text-muted">{school.motto}</p> : null}
            </div>
          </div>
          <ul className="mt-4 space-y-1.5 text-ink-soft">
            {contact.address ? (
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden="true" />
                <span className="min-w-0 whitespace-pre-line">{contact.address}</span>
              </li>
            ) : null}
            {contact.phone ? (
              <li className="flex items-center gap-2">
                <Phone className="size-4 shrink-0 text-muted" aria-hidden="true" />
                <a href={`tel:${contact.phone.replace(/\s+/g, '')}`} className="hover:underline">
                  {contact.phone}
                </a>
              </li>
            ) : null}
            {contact.email ? (
              <li className="flex min-w-0 items-center gap-2">
                <Mail className="size-4 shrink-0 text-muted" aria-hidden="true" />
                <a href={`mailto:${contact.email}`} className="min-w-0 hover:underline">
                  {contact.email}
                </a>
              </li>
            ) : null}
          </ul>
        </div>
        <div className="flex flex-col items-start gap-3 @3xl:items-end">
          <ThemeToggle className="border border-line" />
          <p className="text-muted">
            © {year} {school.name}
          </p>
          <p className="text-muted">
            {websiteUrl ? (
              <a href={websiteUrl} className="hover:text-ink hover:underline">
                {t('Powered by {{name}}', { name: APP_NAME })}
              </a>
            ) : (
              t('Powered by {{name}}', { name: APP_NAME })
            )}
          </p>
        </div>
      </div>
    </footer>
  )
}
